import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import Header from "../components/app/Header";
import ToolCard from "../components/app/ToolCard";
import { Search, Filter, Sparkles, Zap, Compass, ClipboardCheck, Lock, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const PROGRAMS = [
  {
    id: "action",
    label: "4-3-2-1 Action Program",
    short: "4-3-2-1",
    Icon: Zap,
    blurb: "Your hands-on agents for the next 90 days — strategy, offers, marketing, ops.",
  },
  {
    id: "mentorship",
    label: "6-Month Mentorship",
    short: "6-Month Mentorship",
    Icon: Compass,
    blurb: "Deeper systems unlocked over the 6-month journey — apps, people, automation, legal.",
  },
];

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [program, setProgram] = useState("action");

  useEffect(() => {
    api
      .get("/tools", { params: { _: Date.now() } })
      .then((res) => setTools(res.data))
      .finally(() => setLoading(false));
  }, []);

  // Tools restricted to the active program
  const programTools = useMemo(
    () => tools.filter((t) => (t.program || "action") === program),
    [tools, program],
  );

  const categories = useMemo(() => {
    const set = new Set(programTools.map((t) => t.category));
    return ["all", ...Array.from(set)];
  }, [programTools]);

  const filtered = useMemo(() => {
    return programTools.filter((t) => {
      if (cat !== "all" && t.category !== cat) return false;
      if (q && !`${t.name} ${t.description}`.toLowerCase().includes(q.toLowerCase()))
        return false;
      return true;
    });
  }, [programTools, q, cat]);

  // Reset the category chip when the user switches programs (otherwise a
  // chip that doesn't exist in the new program would silently hide everything).
  useEffect(() => {
    setCat("all");
  }, [program]);

  const unlockedCount = programTools.filter((t) => t.has_access && t.status === "unlocked").length;
  const totalCount = programTools.length;
  // Counts per program for the tab badges
  const programCounts = useMemo(() => {
    const counts = { action: 0, mentorship: 0 };
    tools.forEach((t) => {
      const p = t.program || "action";
      if (p in counts) counts[p] += 1;
    });
    return counts;
  }, [tools]);

  const canUseGapsAudit = user?.status !== "revoked";

  const openGapsAudit = () => {
    if (!canUseGapsAudit) {
      toast.error("Gaps Audit is available to active members only");
      return;
    }
    nav("/gaps-audit");
  };

  return (
    <div className="min-h-screen">
      <Header />

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        {/* Hero strip */}
        <section className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="md:col-span-2 relative overflow-hidden rounded-2xl border border-zinc-800/80 bg-zinc-950/40 p-8 grain">
            <div className="absolute -right-10 -top-10 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl" />
            <div className="relative">
              <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-400 mb-3">
                // business breakthrough hub / {new Date().toLocaleDateString("en-US", { weekday: "long" })}
              </div>
              <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-black leading-[1.05]">
                Good to see you,{" "}
                <span className="text-emerald-400">{user?.name?.split(" ")[0]}</span>.
              </h1>
              <p className="mt-3 text-zinc-400 max-w-xl">
                Pick an agent to begin. Each one is hand-prompted for a single
                business task — so the conversation stays sharp.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <StatCard
              label="Unlocked"
              value={unlockedCount}
              accent="emerald"
            />
            <StatCard label="Total Agents" value={totalCount} accent="gold" />
            <StatCard
              label="Plan"
              value={user?.role === "admin" ? "Admin" : "Member"}
              accent="gold"
            />
            <StatCard
              label="Status"
              value="Active"
              accent="emerald"
            />
          </div>
        </section>

        <GapsAuditEntryCard unlocked={canUseGapsAudit} onOpen={openGapsAudit} />

        {/* Program tabs */}
        <section className="mb-8" data-testid="program-tabs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PROGRAMS.map((p) => {
              const active = program === p.id;
              const count = programCounts[p.id] || 0;
              const Icon = p.Icon;
              return (
                <button
                  key={p.id}
                  data-testid={`program-tab-${p.id}`}
                  onClick={() => setProgram(p.id)}
                  className={`relative group text-left rounded-2xl border p-5 overflow-hidden transition-all ${
                    active
                      ? "border-emerald-500/50 bg-emerald-500/[0.06] shadow-[0_0_0_1px_rgba(16,185,129,0.15)]"
                      : "border-zinc-800 bg-zinc-950/40 hover:border-zinc-700"
                  }`}
                >
                  {active && (
                    <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-400/70 to-transparent" />
                  )}
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border ${
                        active
                          ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                          : "bg-zinc-900/60 border-zinc-800 text-zinc-400 group-hover:text-white"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3
                          className={`font-display text-lg tracking-tight font-bold ${
                            active ? "text-white" : "text-zinc-200"
                          }`}
                        >
                          {p.label}
                        </h3>
                        <span
                          className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full border ${
                            active
                              ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30"
                              : "bg-zinc-900 text-zinc-400 border-zinc-800"
                          }`}
                        >
                          {count}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                        {p.blurb}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Filters */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-8">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid="tools-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search agents..."
              className="w-full bg-zinc-950/60 border border-zinc-800 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white outline-none focus:border-emerald-500/50 transition-colors"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto">
            <Filter className="w-4 h-4 text-zinc-500 shrink-0" />
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`cat-${c}`}
                onClick={() => setCat(c)}
                className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.18em] border transition-colors whitespace-nowrap ${
                  cat === c
                    ? "bg-emerald-600 border-emerald-500 text-white"
                    : "bg-transparent border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Cards Grid */}
        {loading ? (
          <div className="text-zinc-500 text-sm">Loading agents...</div>
        ) : filtered.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((t, i) => (
              <ToolCard key={t.id} tool={t} index={i} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const GapsAuditEntryCard = ({ unlocked, onOpen }) => (
  <section
    data-testid="gaps-audit-entry-card"
    className="mb-10 rounded-2xl border border-zinc-800/80 bg-zinc-950/40 p-6 md:p-7 grain relative overflow-hidden"
  >
    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent" />
    <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-emerald-500/10 blur-3xl" />
    <div className="relative grid md:grid-cols-[auto_1fr_auto] gap-5 md:items-center">
      <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 flex items-center justify-center">
        <ClipboardCheck className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-display text-2xl tracking-tight font-black">
            Gaps Audit
          </h3>
          <span
            className={`text-[9px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border ${
              unlocked
                ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/5"
                : "text-amber-300 border-amber-500/30 bg-amber-500/5"
            }`}
          >
            {unlocked ? "General worksheet" : "Active members only"}
          </span>
        </div>
        <p className="text-sm text-zinc-500 leading-relaxed mt-2 max-w-2xl">
          Track recurring business gaps, what each one costs, and the learning
          needed to close it — independent of any program access.
        </p>
      </div>
      <button
        type="button"
        data-testid="open-gaps-audit-btn"
        onClick={onOpen}
        aria-disabled={!unlocked}
        className={`w-full md:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg font-semibold transition-colors ${
          unlocked
            ? "bg-emerald-600 text-white hover:bg-emerald-500"
            : "bg-zinc-900 text-zinc-500 border border-zinc-800 hover:text-amber-300 hover:border-amber-500/30"
        }`}
      >
        {unlocked ? (
          <>Open Gaps Audit <ArrowRight className="w-4 h-4" /></>
        ) : (
          <><Lock className="w-4 h-4" /> Account revoked</>
        )}
      </button>
    </div>
  </section>
);

const StatCard = ({ label, value, small, accent }) => (
  <div className="rounded-2xl border border-zinc-800/80 bg-zinc-950/40 p-5 grain relative overflow-hidden">
    <div
      className={`absolute top-0 left-0 right-0 h-px ${
        accent === "gold"
          ? "bg-gradient-to-r from-transparent via-amber-400/40 to-transparent"
          : "bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent"
      }`}
    />
    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
      {label}
    </div>
    <div
      className={`font-display font-black tracking-tighter mt-2 ${
        small ? "text-xl" : "text-3xl"
      } ${accent === "gold" ? "text-amber-300" : "text-white"}`}
    >
      {value}
    </div>
  </div>
);

const EmptyState = () => (
  <div className="text-center py-24 border border-dashed border-zinc-800 rounded-2xl">
    <Sparkles className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
    <div className="font-display text-xl tracking-tight">No agents yet</div>
    <div className="text-sm text-zinc-500 mt-1">
      Ask your admin to add agents to the hub.
    </div>
  </div>
);
